import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {ConfigProvider, Dropdown, Menu, Switch} from "antd";
import {DriverDeliveriesContext} from "./DriverDeliveriesContext.jsx";
import "../../dashboards/DeliveryProviderDashboard/DeliveryProviderDeliveries/delivery-provider-deliveries.mobile.css";
import {uniqBy} from "lodash";
import {
    DeliveriesShortcuts,
    deliveryItemHeaderConfig,
    DeliveryShortcutsKeys
} from "../../dashboards/DeliveryProviderDashboard/DeliveryProviderDeliveries/consts.jsx";
import {ItemsListDateRangeMobile} from "../../components/OrdersDateRangeTable/ItemListViewMobile/ItemsListDateRange.mobile.jsx";
import {openWindow} from "../../http/WindowOpener.jsx";
import {StringBuilder} from "../../AppUtils.js";
import moment from "moment";
import {FilterOutlined, CheckOutlined} from "@ant-design/icons";

export const DriverDeliveriesMobile = () => {
    const {
        deliveries,
        isLoading,
        setCurrentPage,
        totalDeliveriesCount,
        searchDate,
        showDeliveredDeliveries,
        setShowDeliveredDeliveries
    } = useContext(DriverDeliveriesContext);
    const [deliveriesLoaded, setDeliveriesLoaded] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [selectedCompanies, setSelectedCompanies] = useState([]);

    const deliveriesList = useMemo(() => deliveries ?? [], [deliveries]);

    useEffect(() => {
        if (deliveriesList.length) {
            setDeliveriesLoaded(prevDeliveries =>
                uniqBy([...prevDeliveries, ...deliveriesList], delivery => delivery?.deliveryId)
            );
        }
    }, [deliveriesList]);

    const searchDateStart = searchDate?.[0];
    const searchDateEnd = searchDate?.[1];

    useEffect(() => {
        setDeliveriesLoaded([]);
        setSelectedCompanies([]);
    }, [searchDateStart, searchDateEnd, showDeliveredDeliveries]);

    const companyOptions = useMemo(() => {
        const names = deliveriesLoaded?.map(delivery => delivery?.companyName)?.filter(Boolean) ?? [];

        return Array.from(new Set(names)).sort((a, b) => a?.localeCompare(b));
    }, [deliveriesLoaded]);

    useEffect(() => {
        setSelectedCompanies(prev => prev.filter(name => companyOptions.includes(name)));
    }, [companyOptions]);

    const searchFilteredDeliveries = useMemo(
        () =>
            deliveriesLoaded?.filter(({companyName, driverId, proposalId, price, order, destinationAddress}) =>
                [
                    companyName,
                    driverId,
                    String(proposalId),
                    String(price),
                    order.providerBrand,
                    destinationAddress?.city,
                    destinationAddress?.address
                ].some(property => property?.toLowerCase()?.includes(searchInput?.toLowerCase()))
            ),
        [searchInput, deliveriesLoaded]
    );

    const filteredDeliveries = useMemo(() => {
        if (!selectedCompanies.length) {
            return searchFilteredDeliveries;
        }

        return searchFilteredDeliveries?.filter(delivery => selectedCompanies.includes(delivery?.companyName));
    }, [searchFilteredDeliveries, selectedCompanies]);

    const toggleCompanyFilter = name => {
        setSelectedCompanies(prev =>
            prev.includes(name) ? prev.filter(company => company !== name) : [...prev, name]
        );
    };

    const companyFilterMenu = (
        <Menu>
            {companyOptions.length ? (
                companyOptions.map(name => {
                    const isActive = selectedCompanies.includes(name);
                    return (
                        <Menu.Item key={name} onClick={() => toggleCompanyFilter(name)}>
                            <div className="driver-deliveries-company-filter-option">
                                <span
                                    className={`driver-deliveries-company-filter-checkbox ${
                                        isActive ? "checked" : ""
                                    }`}>
                                    {isActive && <CheckOutlined />}
                                </span>
                                <span>{name}</span>
                            </div>
                        </Menu.Item>
                    );
                })
            ) : (
                <Menu.Item disabled key="empty">
                    אין חברות לסינון
                </Menu.Item>
            )}
            {selectedCompanies.length ? (
                <Menu.Item key="clear" onClick={() => setSelectedCompanies([])}>
                    איפוס סינון
                </Menu.Item>
            ) : null}
        </Menu>
    );

    const companyFilterButton = (
        <Dropdown overlay={companyFilterMenu} trigger={["click"]} placement="bottomRight">
            <button
                type="button"
                className={`driver-deliveries-company-filter-btn ${selectedCompanies.length ? "active" : ""}`}>
                <FilterOutlined />
            </button>
        </Dropdown>
    );

    const handleShowDeliveredDeliveriesToggle = useCallback(checked => {
        setShowDeliveredDeliveries(prev => !prev);
    }, []);

    return (
        <div className="delivery-provider-deliveries-mobile-page">
            <ConfigProvider direction="rtl">
                <ItemsListDateRangeMobile
                    singleDay={true}
                    title={(() => {
                        const startDate = moment(searchDate[0]);
                        const endDate = moment(searchDate[1]);

                        const formattedStart = startDate.format("DD/MM/YYYY");
                        const formattedEnd = endDate.format("DD/MM/YYYY");

                        return new StringBuilder(totalDeliveriesCount || "0")
                            .append("משלוחים")
                            .append(formattedStart)
                            .append(
                                startDate.isSame(endDate, "day")
                                    ? ""
                                    : new StringBuilder(" - ").append(formattedEnd).toString()
                            )
                            .toString();
                    })()}
                    extraContent={
                        <div className="driver-deliveries-show-delivered-deliveries-toggle-container">
                            <Switch
                                onChange={handleShowDeliveredDeliveriesToggle}
                                checked={!showDeliveredDeliveries}
                                className="driver-deliveries-show-delivered-deliveries-toggle-switch"
                                title={!showDeliveredDeliveries ? "משלוחים פתוחים" : "משלוחים סגורים"}
                            />
                            <span className="driver-deliveries-show-delivered-deliveries-toggle-switch">
                                {!showDeliveredDeliveries ? "משלוחים פתוחים" : "משלוחים סגורים"}
                            </span>
                        </div>
                    }
                    data={filteredDeliveries ?? []}
                    shortcutSearchOptions={DeliveriesShortcuts}
                    defaultShortcutSearchOptions={DeliveryShortcutsKeys.todayDeliveries}
                    allowDeselectShortcutSearchOptions={false}
                    isLoading={isLoading}
                    showSearchByCreatedAt={false}
                    disableFutureDates={false}
                    totalResultsAmount={totalDeliveriesCount}
                    useLazyLoading={true}
                    onLastItemReach={() => setCurrentPage(prevPageCount => prevPageCount + 1)}
                    onItemClick={deliveryProvider =>
                        openWindow(`/delivery/driver/${deliveryProvider?.deliveryId}`, "_self")
                    }
                    panelHeader={deliveryItemHeaderConfig({hideDriverTag: true})}
                    onInputSearch={value => setSearchInput(value)}
                    panelBody={{
                        actions: [],
                        style: {
                            borderColor: "var(--secondary-color)"
                        }
                    }}
                    // headerExtra={companyFilterButton}
                />
            </ConfigProvider>
        </div>
    );
};
